import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';

interface PendingUser {
  id: string;
  full_name: string | null;
  requested_role: string | null;
  org_name_requested: string | null;
  created_at: string;
  email?: string;
}

interface Props {
  onCountChange?: (count: number) => void;
}

export default function PendingApprovals({ onCountChange }: Props) {
  const [pending, setPending] = useState<PendingUser[]>([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' | 'warning' } | null>(null);

  const showToast = (msg: string, type: 'success' | 'error' | 'warning' = 'success') => {
    setToast({ msg, type });
    setTimeout(() => setToast(null), 3500);
  };

  const fetchPending = useCallback(async () => {
    setLoading(true);
    // Fetch unapproved profiles
    const { data: profiles, error } = await supabase
      .from('user_profiles')
      .select('id, full_name, requested_role, org_name_requested, created_at')
      .eq('is_approved', false)
      .order('created_at', { ascending: true });

    if (error) {
      showToast('Failed to load pending registrations.', 'error');
      setLoading(false);
      return;
    }

    // Fetch emails from auth.users via Supabase (using service via direct join not available — use email from auth)
    // We'll fetch email using a separate RPC or just display without email
    const items = (profiles ?? []) as PendingUser[];

    // Try to fetch emails if possible
    const enriched: PendingUser[] = await Promise.all(
      items.map(async (p) => {
        try {
          const { data: authUser } = await supabase
            .from('user_profiles')
            .select('id')
            .eq('id', p.id)
            .maybeSingle();
          return { ...p, email: undefined };
        } catch {
          return p;
        }
      })
    );

    setPending(enriched);
    onCountChange?.(enriched.length);
    setLoading(false);
  }, [onCountChange]);

  useEffect(() => { fetchPending(); }, [fetchPending]);

  const handleApprove = async (userId: string) => {
    setProcessing(userId);
    const user = pending.find(u => u.id === userId);
    const isPartner = user?.requested_role === 'partner';

    const { error } = await supabase
      .from('user_profiles')
      .update({ is_approved: true })
      .eq('id', userId);

    if (error) {
      showToast(`Approval failed: ${error.message}`, 'error');
    } else {
      if (isPartner) {
        showToast('Approved! Now go to User Management and link an organization.', 'warning');
      } else {
        showToast('Account approved successfully!');
      }
      setPending(prev => {
        const updated = prev.filter(u => u.id !== userId);
        onCountChange?.(updated.length);
        return updated;
      });
    }
    setProcessing(null);
  };

  const handleReject = async (userId: string) => {
    setProcessing(userId);
    // Delete the user profile and auth user
    const { error } = await supabase
      .from('user_profiles')
      .update({ role: 'partner', is_approved: false, requested_role: 'rejected' })
      .eq('id', userId);

    if (error) {
      showToast(`Action failed: ${error.message}`, 'error');
    } else {
      showToast('Registration rejected.');
      setPending(prev => {
        const updated = prev.filter(u => u.id !== userId);
        onCountChange?.(updated.length);
        return updated;
      });
    }
    setProcessing(null);
  };

  const roleLabel: Record<string, string> = {
    partner: 'Partner (NGO)',
    manager: 'Manager',
    admin: 'Administrator',
    rejected: 'Rejected',
  };

  const roleColor: Record<string, string> = {
    partner: 'bg-teal-50 text-teal-700',
    manager: 'bg-indigo-50 text-indigo-700',
    admin: 'bg-amber-50 text-amber-700',
  };

  return (
    <div className="bg-white rounded-lg border border-cool-gray-200">
      {/* Toast */}
      {toast && (
        <div className={`fixed top-20 right-6 z-[100] flex items-center gap-2 px-4 py-3 rounded-md text-sm font-medium shadow-sm max-w-xs ${
          toast.type === 'success' ? 'bg-emerald-600 text-white'
          : toast.type === 'warning' ? 'bg-amber-500 text-white'
          : 'bg-red-600 text-white'
        }`}>
          <span className="w-4 h-4 flex items-center justify-center shrink-0">
            <i className={
              toast.type === 'success' ? 'ri-check-line'
              : toast.type === 'warning' ? 'ri-alert-line'
              : 'ri-close-circle-line'
            } />
          </span>
          {toast.msg}
        </div>
      )}

      <div className="px-6 py-5 border-b border-cool-gray-200 flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-navy">Partner Registration Requests</h2>
          <p className="text-xs text-navy/40 mt-0.5">Review and approve new partner/manager accounts</p>
        </div>
        <button
          onClick={fetchPending}
          disabled={loading}
          className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-medium text-navy/50 border border-cool-gray-200 rounded-md hover:bg-cool-gray-50 transition-colors cursor-pointer whitespace-nowrap"
        >
          <span className="w-4 h-4 flex items-center justify-center">
            <i className={`ri-refresh-line ${loading ? 'animate-spin' : ''}`} />
          </span>
          Refresh
        </button>
      </div>

      {/* Workflow reminder banner — visible when partner requests exist */}
      {!loading && pending.some(u => u.requested_role === 'partner') && (
        <div className="mx-6 mt-4 mb-1 flex items-start gap-3 px-4 py-3 bg-amber-50 border border-amber-200 rounded-md">
          <span className="w-5 h-5 flex items-center justify-center shrink-0 mt-0.5">
            <i className="ri-information-line text-amber-600 text-base" />
          </span>
          <div className="text-xs text-amber-800 leading-relaxed">
            <span className="font-semibold">Reminder — 2-step workflow for partners:</span>
            {' '}After approving a partner here, go to{' '}
            <span className="font-semibold">User Management</span>{' '}
            and link the correct organization to their account.
            Without this step the partner will not be able to access their organization page.
          </div>
        </div>
      )}

      {loading ? (
        <div className="py-16 flex items-center justify-center">
          <i className="ri-loader-4-line text-2xl text-navy/30 animate-spin" />
        </div>
      ) : pending.length === 0 ? (
        <div className="py-16 text-center">
          <div className="w-12 h-12 flex items-center justify-center bg-emerald-50 rounded-full mx-auto mb-3">
            <i className="ri-check-double-line text-emerald-500 text-xl" />
          </div>
          <p className="text-sm font-medium text-navy/60">No pending registrations</p>
          <p className="text-xs text-navy/30 mt-1">All registrations have been processed</p>
        </div>
      ) : (
        <div className="divide-y divide-cool-gray-100">
          {pending.map(user => (
            <div key={user.id} className="px-6 py-4 flex flex-col sm:flex-row sm:items-center gap-4">
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-1 flex-wrap">
                  <span className="text-sm font-semibold text-navy">
                    {user.full_name || 'Unknown'}
                  </span>
                  {user.requested_role && (
                    <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${roleColor[user.requested_role] ?? 'bg-stone-100 text-stone-600'}`}>
                      {roleLabel[user.requested_role] ?? user.requested_role}
                    </span>
                  )}
                </div>
                {user.org_name_requested && (
                  <p className="text-xs text-navy/50 flex items-center gap-1 mb-0.5">
                    <i className="ri-building-line" />
                    {user.org_name_requested}
                  </p>
                )}
                <p className="text-xs text-navy/30">
                  Registered: {new Date(user.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
                </p>
                {user.requested_role === 'partner' && (
                  <p className="text-xs text-amber-600 flex items-center gap-1 mt-1.5 font-medium">
                    <span className="w-3.5 h-3.5 flex items-center justify-center">
                      <i className="ri-link text-amber-500" />
                    </span>
                    After approval — link organization in User Management
                  </p>
                )}
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => handleReject(user.id)}
                  disabled={processing === user.id}
                  className="inline-flex items-center gap-1.5 px-3 py-2 text-xs font-semibold text-red-600 border border-red-200 rounded-md hover:bg-red-50 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-close-line" />
                  Reject
                </button>
                <button
                  onClick={() => handleApprove(user.id)}
                  disabled={processing === user.id}
                  className="inline-flex items-center gap-1.5 px-4 py-2 text-xs font-semibold text-white bg-emerald-600 rounded-md hover:bg-emerald-700 transition-colors disabled:opacity-50 cursor-pointer whitespace-nowrap"
                >
                  {processing === user.id ? (
                    <i className="ri-loader-4-line animate-spin" />
                  ) : (
                    <i className="ri-check-line" />
                  )}
                  Approve
                </button>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
